import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, ScrollText, ArrowDown, Check, X } from "lucide-react";
import {
  TERMS_VERSION, LEGAL_EFFECTIVE, LEGAL_ENTITY, LEGAL_DBA, TERMS_SUMMARY, TERMS_SECTIONS,
} from "@/legal/content";

// Gate in front of every Stripe checkout: the terms must be scrolled and ticked, and the
// acceptance is recorded server-side before the caller is allowed to continue.
export const TermsDialog = ({ open, onClose, onAccepted, context = "subscription",
  record = true, continueLabel = "Agree & continue to secure checkout" }) => {
  const scroller = useRef(null);
  const [atEnd, setAtEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setAtEnd(false);
      setAgreed(false);
      setError("");
      setBusy(false);
    }
  }, [open]);

  // A viewport tall enough to show the whole document has nothing left to scroll.
  useEffect(() => {
    if (!open) return;
    const el = scroller.current;
    if (el && el.scrollHeight <= el.clientHeight + 48) setAtEnd(true);
  }, [open]);

  if (!open) return null;

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) setAtEnd(true);
  };

  const jumpToEnd = () => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      if (record) {
        await api.post("/legal/accept", { terms_version: TERMS_VERSION, context });
      }
      await onAccepted();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail)
        || "Could not record your acceptance — please try again");
      setBusy(false);
    }
  };

  return (
    <div data-testid="terms-dialog"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-royal-deep/70 backdrop-blur-sm p-0 sm:p-6">
      <div className="flex w-full max-w-2xl max-h-[94vh] sm:max-h-[88vh] flex-col overflow-hidden
                      rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4 sm:px-7 sm:py-5">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-2.5 py-1
                             text-[10px] font-bold uppercase tracking-[0.16em] text-royal-deep">
              <ScrollText className="h-3 w-3" /> Required before checkout
            </span>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-royal-deep mt-2.5">
              Subscription Terms of Service
            </h2>
            <p className="text-[11px] text-muted-foreground mt-1">
              {LEGAL_ENTITY} d/b/a {LEGAL_DBA} · Version {TERMS_VERSION} · Effective {LEGAL_EFFECTIVE}
            </p>
          </div>
          <button type="button" data-testid="terms-dialog-close" onClick={onClose}
            className="shrink-0 rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-royal-deep transition-colors duration-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-border bg-secondary/50 px-5 py-3.5 sm:px-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-royal">In plain English</p>
          <ul className="mt-2 space-y-1.5">
            {TERMS_SUMMARY.map((s) => (
              <li key={s} className="flex items-start gap-2 text-xs text-royal-deep">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {s}
              </li>
            ))}
          </ul>
        </div>

        <div ref={scroller} onScroll={onScroll} data-testid="terms-dialog-scroll"
          className="relative flex-1 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
          {TERMS_SECTIONS.map((sec) => (
            <section key={sec.title} className="mb-5">
              <h3 className="font-display text-sm font-bold text-royal-deep">{sec.title}</h3>
              {sec.body.map((p, i) => (
                <p key={i} className="mt-2 text-xs leading-relaxed text-muted-foreground">{p}</p>
              ))}
            </section>
          ))}
          <p data-testid="terms-dialog-end" className="border-t border-border pt-4 text-xs font-semibold text-royal-deep">
            End of Terms of Service — Version {TERMS_VERSION}, effective {LEGAL_EFFECTIVE}.
          </p>
        </div>

        <div className="border-t border-border px-5 py-4 sm:px-7 sm:py-5">
          {!atEnd && (
            <button type="button" data-testid="terms-dialog-jump" onClick={jumpToEnd}
              className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold transition-colors duration-200">
              <ArrowDown className="h-3.5 w-3.5" /> Scroll to the end to enable acceptance
            </button>
          )}
          <label className={`flex items-start gap-3 rounded-lg border p-3.5 transition-colors duration-200
            ${atEnd ? "border-gold/70 bg-gold/5 cursor-pointer" : "border-border bg-muted/50 opacity-60"}`}>
            <input type="checkbox" data-testid="terms-agree-checkbox" disabled={!atEnd}
              checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#1b2a63]" />
            <span className="text-xs leading-relaxed text-royal-deep">
              I have read and agree to the Terms of Service and the{" "}
              <a href="/privacy" target="_blank" rel="noreferrer"
                className="font-semibold text-royal underline">Privacy Policy</a>, and I authorize
              recurring charges to my payment method after the 14-day free trial until I cancel. I am
              authorized to accept these terms for my company.
            </span>
          </label>

          {error && <p data-testid="terms-dialog-error" className="mt-3 text-xs text-red-600">{error}</p>}

          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <a href="/terms" target="_blank" rel="noreferrer" data-testid="terms-dialog-fullpage"
              className="text-center text-xs font-semibold text-muted-foreground hover:text-royal transition-colors duration-200 sm:mr-auto">
              Open as a full page / print
            </a>
            <Button variant="outline" data-testid="terms-decline-btn" onClick={onClose}
              className="h-11 border-border text-royal-deep">
              Not now
            </Button>
            <Button data-testid="terms-accept-btn" disabled={!agreed || busy} onClick={confirm}
              className="h-11 bg-royal hover:bg-royal-deep text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : continueLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsDialog;
